import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { invoicesApi, teamApi, projectsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Receipt, Send, CheckCircle, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    tax_rate: 16,
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, teamRes, projectsRes] = await Promise.all([
        invoicesApi.getAll(),
        teamApi.getMembers().catch(() => ({ data: [] })),
        projectsApi.getAll(),
      ]);
      setInvoices(invoicesRes.data);
      setClients(teamRes.data.filter(u => u.role === 'client'));
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoicesApi.create(formData);
      toast.success('Factura creada');
      setDialogOpen(false);
      setFormData({
        client_id: '',
        project_id: '',
        items: [{ description: '', quantity: 1, unit_price: 0 }],
        tax_rate: 16,
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Error al crear factura');
    }
  };

  const handleSend = async (invoiceId) => {
    try {
      await invoicesApi.send(invoiceId);
      toast.success('Factura enviada (CFDI timbrado - SIMULADO)');
      fetchData();
    } catch (error) {
      toast.error('Error al enviar factura');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await invoicesApi.markPaid(invoiceId);
      toast.success('Factura marcada como pagada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar factura');
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await invoicesApi.delete(invoiceId);
      toast.success('Factura eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar factura');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }],
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout title="Facturas">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Facturas">
      <div className="space-y-6" data-testid="invoices-page">
        {/* SAT Notice */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="text-amber-400" size={24} />
            <div>
              <p className="text-amber-200 font-medium">Integración SAT/CFDI (SIMULADA)</p>
              <p className="text-amber-200/70 text-sm">
                El timbrado de facturas requiere un proveedor PAC. Configura tus credenciales de Facturapi u otro PAC para habilitar la generación real de CFDI.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-zinc-400">Administra facturas y CFDI para cumplimiento fiscal mexicano</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="create-invoice-btn">
                <Plus size={20} className="mr-2" />
                Nueva Factura
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-white/10 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white">Crear Factura</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Cliente</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                    >
                      <SelectTrigger className="bg-[#1A1A1A] border-transparent">
                        <SelectValue placeholder="Seleccionar cliente" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {clients.length === 0 ? (
                          <SelectItem value="no-clients" disabled>Sin clientes disponibles</SelectItem>
                        ) : (
                          clients.map((client) => (
                            <SelectItem key={client.user_id} value={client.user_id}>
                              {client.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Conceptos</Label>
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder="Descripción"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          className="flex-1 bg-[#1A1A1A] border-transparent"
                          required
                        />
                        <Input
                          type="number"
                          placeholder="Cant."
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="w-20 bg-[#1A1A1A] border-transparent"
                          min="1"
                          required
                        />
                        <Input
                          type="number"
                          placeholder="Precio"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          className="w-28 bg-[#1A1A1A] border-transparent"
                          min="0"
                          step="0.01"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          className="text-zinc-500 hover:text-red-400"
                          disabled={formData.items.length === 1}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2">
                    <Plus size={16} className="mr-1" />
                    Agregar Concepto
                  </Button>
                </div>

                {/* Totals */}
                <div className="p-4 rounded-lg bg-[#1A1A1A] space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="text-white">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">IVA ({formData.tax_rate}%)</span>
                    <span className="text-white">{formatCurrency(calculateSubtotal() * (formData.tax_rate / 100))}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-white/10">
                    <span className="text-white">Total</span>
                    <span className="text-orange-400">{formatCurrency(calculateSubtotal() * (1 + formData.tax_rate / 100))}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={!formData.client_id || formData.items.some(i => !i.description)}
                  data-testid="invoice-submit"
                >
                  Crear Factura
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Invoices Table */}
        <Card className="bg-[#141414] border-white/5">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Factura #</TableHead>
                  <TableHead className="text-zinc-400">Fecha</TableHead>
                  <TableHead className="text-zinc-400">Total</TableHead>
                  <TableHead className="text-zinc-400">Estado</TableHead>
                  <TableHead className="text-zinc-400">CFDI</TableHead>
                  <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Receipt className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-400">Aún no hay facturas</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.invoice_id} className="border-white/5" data-testid={`invoice-row-${invoice.invoice_id}`}>
                      <TableCell className="font-medium text-white">{invoice.invoice_number}</TableCell>
                      <TableCell className="text-zinc-400">
                        {format(new Date(invoice.created_at), 'd MMM, yyyy', { locale: es })}
                      </TableCell>
                      <TableCell className="text-white">{formatCurrency(invoice.total)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                          invoice.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {invoice.status === 'paid' ? 'Pagada' : invoice.status === 'sent' ? 'Enviada' : 'Borrador'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          invoice.cfdi_status === 'stamped' ? 'bg-green-500/20 text-green-400' :
                          invoice.cfdi_status === 'error' ? 'bg-red-500/20 text-red-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {invoice.cfdi_status === 'stamped' ? 'Timbrada' : invoice.cfdi_status === 'error' ? 'Error' : 'Pendiente'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {invoice.status === 'draft' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSend(invoice.invoice_id)}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Send size={16} className="mr-1" />
                              Enviar
                            </Button>
                          )}
                          {invoice.status === 'sent' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMarkPaid(invoice.invoice_id)}
                              className="text-green-400 hover:text-green-300"
                            >
                              <CheckCircle size={16} className="mr-1" />
                              Pagada
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(invoice.invoice_id)}
                            className="text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default InvoicesPage;
